import { extractRegionFromAccessToken } from '@heidi/auth/tokens';
import { isTauri } from '@heidi/runtime/platform';
import { useUser } from '@heidi/workspace/data';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type {
  DictationFeedbackCallbacks,
  DictationFeedbackRegion,
  DictationFeedbackSurface,
} from '../../contracts/dictation-feedback.types';
import { submitDictationFeedback } from '../../data/api/feedback';
import { getDictationDataConfig } from '../../data/config';
import { setDictationFeedbackRatingInLogsCache } from '../../data/mutation/setDictationFeedbackRatingInLogsCache';
import { useQueryDictationPostProcessingVariant } from '../../data/query/useQueryDictationPostProcessingVariant';
import {
  isDictateUserFeedbackEnabled,
  useIsDictateUserFeedbackEnabled,
} from '../../flags';

export const toDictationFeedbackRegion = (
  token: string | null | undefined
): DictationFeedbackRegion | undefined => {
  const coarseRegion = token
    ? extractRegionFromAccessToken(token)?.trim().toLowerCase()
    : undefined;

  return coarseRegion === 'au' || coarseRegion === 'uk' || coarseRegion === 'us'
    ? coarseRegion
    : undefined;
};

/**
 * Shared feedback submission wiring for desktop and web. The auth token comes
 * through the package's existing reactive app seam, and the callback is absent
 * while the rollout gate is off so neither surface can submit around hidden UI.
 * The synchronous fallback keeps submission aligned with non-React flows that
 * may show the prompt before the reactive flag value has finished hydrating.
 */
export const useDictationFeedbackSubmitHandler = (
  surface: DictationFeedbackSurface = 'widget'
): DictationFeedbackCallbacks['onSubmitFeedback'] | undefined => {
  const enabled = useIsDictateUserFeedbackEnabled() || isDictateUserFeedbackEnabled();
  const queryClient = useQueryClient();
  const token = getDictationDataConfig().useAuthToken();
  const { data: dictationSettings } = useUser((user) => user.dictation);
  const region = useMemo(() => toDictationFeedbackRegion(token), [token]);
  const { refetch: refetchExperimentVariant } = useQueryDictationPostProcessingVariant({
    isDesktop: isTauri(),
    outputLanguages: dictationSettings?.output_languages,
    enabled: false,
  });

  const submit = useCallback<NonNullable<DictationFeedbackCallbacks['onSubmitFeedback']>>(
    async (feedback) => {
      const experimentVariantData =
        (dictationSettings?.output_languages?.length ?? 0) > 0
          ? (await refetchExperimentVariant()).data
          : undefined;
      const experimentVariant = experimentVariantData?.variant;

      const savedFeedback = await submitDictationFeedback(feedback, {
        surface,
        ...(experimentVariant ? { experimentVariant } : {}),
        inputLanguages: dictationSettings?.input_languages ?? [],
        outputLanguages: dictationSettings?.output_languages ?? [],
        ...(region ? { region } : {}),
      });
      if (feedback.dictationId) {
        setDictationFeedbackRatingInLogsCache(
          queryClient,
          feedback.dictationId,
          savedFeedback.rating
        );
      }
    },
    [
      dictationSettings?.input_languages,
      dictationSettings?.output_languages,
      queryClient,
      refetchExperimentVariant,
      region,
      surface,
    ]
  );

  return enabled ? submit : undefined;
};
